import { Form, Layout, Spin } from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useGetHotelInfo } from 'app/hooks/data/core/admin/hotels/useGetHotelInfo';
import { useUpdateHotel } from 'app/hooks/data/hotels/useHotel';
import { ChannelManager } from 'app/typings/channelManagers';
import type { Hotel, HotelInfo } from 'app/typings/hotels/Hotel/Hotel';
import { RateMode } from 'app/typings/rateModes';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import styles from './General.module.scss';
import { GENERAL_SECTIONS } from './GeneralSection';

const { Content } = Layout;

type GeneralProps = {
  headerMenu: React.ReactNode;
  headerExtra?: React.ReactNode[];
  hotelId: number;
};

const buildHotelUpdate = (
  hotel: HotelInfo | undefined,
  values: Hotel
): Partial<Hotel> => {
  const merged = { ...hotel, ...values };

  const channelManager =
    merged.channelManager === ChannelManager.NONE
      ? undefined
      : merged.channelManager;

  return {
    id: merged.id,
    groupId: merged.groupId,
    name: merged.name,
    stars: merged.stars,
    roomCount: merged.roomCount,
    address: {
      ...hotel?.address,
      ...values.address,
      subCity: values.address?.subCity?.trim() || undefined,
    },
    location: { ...hotel?.location, ...values.location },
    touristTaxId: merged.touristTaxId,
    subway: merged.subway,
    managerId: merged.managerId,
    parentHotelId: merged.parentHotelId,
    connectedBarBb: merged.connectedBarBb,
    channelManager,
    channelManagerCredentials:
      channelManager === ChannelManager.MEWS
        ? merged.channelManagerCredentials
        : undefined,
    rateMode: channelManager
      ? merged.rateMode || RateMode.MULTI_RATE
      : undefined,
    supplierId:
      channelManager === ChannelManager.DERBYSOFT
        ? merged.supplierId
        : undefined,
  };
};

const GeneralComponent = ({
  headerMenu,
  headerExtra,
  hotelId,
}: GeneralProps) => {
  const [form] = Form.useForm<Hotel>();

  const [, forceUpdate] = useReducer((count: number) => count + 1, 0);

  const { enterEditMode, enterViewMode, mode } =
    useContext(ViewEditModeContext);

  const {
    data: hotel,
    isLoading: isHotelLoading,
    refetch: reloadHotel,
  } = useGetHotelInfo(hotelId);
  const { mutateAsync: updateHotel } = useUpdateHotel(hotelId);

  const [isLoading, setIsLoading] = useState(false);

  const onSave = useCallback(() => {
    setIsLoading(true);
    form.submit();
  }, [form]);

  const resetForm = useCallback(() => {
    if (hotel) {
      form.setFieldsValue(hotel);
    }
  }, [form, hotel]);

  const onCancel = useCallback(() => {
    resetForm();
    enterViewMode();

    if (hotel) {
      form.setFieldsValue(hotel);
    }
  }, [resetForm, enterViewMode, hotel, form]);

  const onFinish = useCallback(
    async (values: Hotel) => {
      try {
        await updateHotel(buildHotelUpdate(hotel, values));
        await reloadHotel();
        enterViewMode();
      } finally {
        setIsLoading(false);
      }
    },
    [updateHotel, hotel, reloadHotel, enterViewMode]
  );

  useEffect(() => {
    if (hotel) {
      form.setFieldsValue(hotel);
    }
  }, [hotel, form]);

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFieldsChange={forceUpdate}
      scrollToFirstError={SCROLL_TO_FIRST_ERROR}
    >
      <SDetailLayout
        title={
          <AssetDetailTitle
            assetTitle={hotel?.name ?? `Hôtel #${hotelId}`}
            hotelName={hotel?.name}
            isNewDataModel={!hotel?.isLegacy}
          />
        }
        backRoute="/hotels"
        mode={mode}
        onEdit={enterEditMode}
        onSave={onSave}
        onCancel={onCancel}
        reset={resetForm}
        headerMenu={headerMenu}
        headerExtra={headerExtra}
        isDirty={form.isFieldsTouched()}
        isValid={form
          .getFieldsError()
          .every((item) => item.errors.length === 0)}
        disabled={isLoading}
        saveDisabled={form
          .getFieldsError()
          .some((item) => item.errors.length > 0)}
        isLoading={isLoading}
      >
        <Spin spinning={isHotelLoading} wrapperClassName={styles.mainContent}>
          <Content className="hotel-detail__content">
            {GENERAL_SECTIONS.map((section) => {
              const SectionComponent = section.component;

              return (
                <SCard
                  key={section.key}
                  title={section.title}
                  className={styles.generalSection}
                >
                  <SectionComponent hotelId={hotelId} />
                </SCard>
              );
            })}
          </Content>
        </Spin>
      </SDetailLayout>
    </Form>
  );
};

export const General = ({ headerMenu, headerExtra, hotelId }: GeneralProps) => {
  return (
    <ViewEditModeContextProvider>
      <GeneralComponent
        headerMenu={headerMenu}
        headerExtra={headerExtra}
        hotelId={hotelId}
      />
    </ViewEditModeContextProvider>
  );
};
